import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, AlertCircle, GripVertical, Eye, EyeOff } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ApiService from '../services/api';
import { mockCasinos } from '../data/mockCasinos';
import sharedData from '../services/sharedData';

// Composant pour un élément triable
const SortableItem = ({ casino, onEdit, onDelete, onToggleVisibility }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: casino.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : casino.hidden ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center space-x-4 p-4 rounded-lg shadow-sm border ${
        isDragging ? 'border-blue-500 shadow-lg bg-white' : 
        casino.hidden ? 'border-gray-300 bg-gray-100' : 'border-gray-200 bg-white'
      }`}
    >
      {/* Drag handle */}
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-1 text-gray-400 hover:text-gray-600"
        title="Glisser pour réordonner"
      >
        <GripVertical size={20} />
      </button>
      
      {/* Visibility toggle */}
      <button
        onClick={() => onToggleVisibility(casino.id)}
        className={`p-2 rounded ${casino.hidden ? 'text-gray-400 hover:bg-gray-200' : 'text-green-600 hover:bg-green-50'}`}
        title={casino.hidden ? 'Afficher sur le site' : 'Masquer du site'}
      >
        {casino.hidden ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
      
      {/* Logo */}
      <img 
        src={casino.logo} 
        alt={casino.name}
        className={`w-12 h-12 object-contain rounded ${casino.hidden ? 'grayscale' : ''}`}
        onError={(e) => {
          e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48'%3E%3Crect fill='%23f3f4f6' width='48' height='48'/%3E%3C/svg%3E";
        }}
      />
      
      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center space-x-2">
          <h3 className={`font-semibold truncate ${casino.hidden ? 'text-gray-500' : 'text-gray-900'}`}>{casino.name}</h3>
          {casino.hidden && (
            <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">Masqué</span>
          )}
        </div>
        <p className="text-sm text-gray-500 truncate">{casino.mainBonus}</p>
        <p className="text-xs text-blue-500 truncate">{casino.url}</p>
      </div>
      
      {/* Rating */}
      <div className="text-center">
        <span className={`font-bold px-2 py-1 rounded ${casino.hidden ? 'bg-gray-200 text-gray-600' : 'bg-green-100 text-green-800'}`}>
          {casino.rating}
        </span>
      </div>
      
      {/* Actions */}
      <div className="flex space-x-2">
        <button
          onClick={() => onEdit(casino)}
          className="p-2 text-blue-600 hover:bg-blue-50 rounded"
          title="Modifier"
        >
          <Edit size={18} />
        </button>
        <button
          onClick={() => onDelete(casino.id)}
          className="p-2 text-red-600 hover:bg-red-50 rounded"
          title="Supprimer"
        >
          <Trash2 size={18} />
        </button>
      </div>
    </div>
  );
};

const AdminDashboard = ({ hideHeader = false }) => {
  const [casinos, setCasinos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState(null);
  const [usingMockData, setUsingMockData] = useState(false);
  const [showHiddenCount, setShowHiddenCount] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    logo: '',
    rating: 5,
    mainBonus: '',
    secondaryBonus: '',
    extraBonus: '',
    score: 5,
    minDeposit: '',
    authorized: '',
    termsDetails: '',
    url: '',
    hidden: false
  });

  // Sensors pour le drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchCasinos();
  }, []);

  const fetchCasinos = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // D'abord vérifier s'il y a des données mises à jour
      if (sharedData.hasUpdatedData()) {
        console.log('Admin: Using shared data from localStorage');
        const sharedCasinos = sharedData.getCasinos();
        setCasinos(sharedCasinos.sort((a, b) => b.rating - a.rating));
        setUsingMockData(true);
        setLoading(false);
        return;
      }
      
      // Try to fetch from API first
      console.log('Admin: Attempting to fetch from API...');
      const data = await ApiService.getCasinos('rating');
      setCasinos(data);
      setUsingMockData(false);
      console.log('Admin: Successfully loaded data from API');
    } catch (err) {
      console.error('Admin: API failed, falling back to mock data:', err);
      
      // Fallback to mock data
      const sortedMockData = [...mockCasinos].sort((a, b) => b.rating - a.rating);
      setCasinos(sortedMockData);
      setUsingMockData(true);
      setError(null); // Clear error since we have fallback data
      
      // Sauvegarder les données initiales dans le système partagé
      sharedData.saveCasinos(sortedMockData);
    } finally {
      setLoading(false);
    }
  };

  // Gestion du drag and drop
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setCasinos((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);
        
        // Sauvegarder l'ordre dans localStorage et l'API
        saveNewOrder(newItems);
        
        return newItems;
      });
    }
  };

  const saveNewOrder = async (orderedCasinos) => {
    try {
      // Mettre à jour les ratings pour refléter l'ordre
      const updatedCasinos = orderedCasinos.map((casino, index) => ({
        ...casino,
        rating: (10 - (index * 0.1)).toFixed(1), // Descendre de 0.1 par position
        score: (10 - (index * 0.1)).toFixed(1)
      }));

      // Essayer de sauvegarder via API
      try {
        for (const casino of updatedCasinos) {
          await ApiService.updateCasino(casino.id, { 
            rating: parseFloat(casino.rating),
            score: parseFloat(casino.score)
          });
        }
        showNotification('Ordre mis à jour avec succès !');
      } catch (apiError) {
        console.log('API update failed, saving to localStorage');
        sharedData.saveCasinos(updatedCasinos);
        showNotification('Ordre sauvegardé localement');
      }
      
      setCasinos(updatedCasinos);
    } catch (err) {
      console.error('Error saving order:', err);
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      logo: '',
      rating: 5,
      mainBonus: '',
      secondaryBonus: '',
      extraBonus: '',
      score: 5,
      minDeposit: '',
      authorized: '',
      termsDetails: '',
      url: ''
    });
  };

  const handleEdit = (casino) => {
    setEditingId(casino.id);
    setFormData({
      name: casino.name,
      logo: casino.logo,
      rating: casino.rating,
      mainBonus: casino.mainBonus,
      secondaryBonus: casino.secondaryBonus || '',
      extraBonus: casino.extraBonus || '',
      score: casino.score,
      minDeposit: casino.minDeposit || '',
      authorized: casino.authorized || '',
      termsDetails: casino.termsDetails || '',
      url: casino.url || ''
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Basic validation
      if (!formData.name || !formData.logo || !formData.mainBonus) {
        showNotification('Veuillez remplir tous les champs obligatoires', 'error');
        return;
      }

      if (usingMockData) {
        // Simulate save in demo mode
        let updatedCasinos;
        
        if (isAddingNew) {
          const newCasino = {
            ...formData,
            id: Date.now().toString(),
            paymentMethods: [],
            gallery: [],
            claimedCodes: null,
            url: formData.url || '#'
          };
          updatedCasinos = [...casinos, newCasino];
          setCasinos(updatedCasinos);
          setIsAddingNew(false);
          showNotification('Casino ajouté avec succès !');
        } else if (editingId) {
          updatedCasinos = casinos.map(casino => 
            casino.id === editingId ? { ...casino, ...formData } : casino
          );
          setCasinos(updatedCasinos);
          setEditingId(null);
          showNotification('Casino mis à jour avec succès !');
        }
        
        // IMPORTANT: Sauvegarder dans le système partagé
        if (updatedCasinos) {
          const saved = sharedData.saveCasinos(updatedCasinos);
          if (saved) {
            console.log('Casino data synchronized with main site');
          } else {
            showNotification('Avertissement: Les changements pourraient ne pas se refléter sur le site principal', 'error');
          }
        }
        
        resetForm();
        return;
      }

      // Real API calls (when API is available)
      if (isAddingNew) {
        const newCasino = await ApiService.createCasino({
          ...formData,
          paymentMethods: [],
          gallery: []
        });
        setCasinos([...casinos, newCasino]);
        setIsAddingNew(false);
        showNotification('Casino créé avec succès !');
      } else if (editingId) {
        const updatedCasino = await ApiService.updateCasino(editingId, formData);
        setCasinos(casinos.map(casino => 
          casino.id === editingId ? updatedCasino : casino
        ));
        setEditingId(null);
        showNotification('Casino mis à jour avec succès !');
      }
      resetForm();
    } catch (err) {
      console.error('Error saving casino:', err);
      showNotification('Échec de la sauvegarde du casino. Veuillez réessayer.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce casino ?')) {
      return;
    }

    try {
      if (usingMockData) {
        // Simulate delete in demo mode
        const updatedCasinos = casinos.filter(casino => casino.id !== id);
        setCasinos(updatedCasinos);
        
        // Sauvegarder dans le système partagé
        const saved = sharedData.saveCasinos(updatedCasinos);
        if (saved) {
          showNotification('Casino supprimé avec succès !');
          console.log('Casino deletion synchronized with main site');
        } else {
          showNotification('Casino supprimé, mais les changements pourraient ne pas se refléter sur le site principal', 'error');
        }
        
        return;
      }

      // Real API call (when API is available)
      await ApiService.deleteCasino(id);
      setCasinos(casinos.filter(casino => casino.id !== id));
      showNotification('Casino supprimé avec succès !');
    } catch (err) {
      console.error('Error deleting casino:', err);
      showNotification('Échec de la suppression du casino. Veuillez réessayer.', 'error');
    }
  };

  // Toggle visibility of a casino
  const handleToggleVisibility = async (id) => {
    try {
      const updatedCasinos = casinos.map(casino => 
        casino.id === id ? { ...casino, hidden: !casino.hidden } : casino
      );
      setCasinos(updatedCasinos);
      
      // Update hidden count
      const hiddenCount = updatedCasinos.filter(c => c.hidden).length;
      setShowHiddenCount(hiddenCount);
      
      // Find the casino that was toggled
      const toggledCasino = updatedCasinos.find(c => c.id === id);
      
      // Try to save via API
      try {
        await ApiService.updateCasino(id, { hidden: toggledCasino.hidden });
        showNotification(toggledCasino.hidden ? 
          `${toggledCasino.name} masqué du site` : 
          `${toggledCasino.name} visible sur le site`
        );
      } catch (apiError) {
        // Fallback to localStorage
        sharedData.saveCasinos(updatedCasinos);
        showNotification(toggledCasino.hidden ? 
          `${toggledCasino.name} masqué (mode local)` : 
          `${toggledCasino.name} visible (mode local)`
        );
      }
    } catch (err) {
      console.error('Error toggling visibility:', err);
      showNotification('Erreur lors du changement de visibilité', 'error');
    }
  };

  const handleCancel = () => {
    setIsAddingNew(false);
    setEditingId(null);
    resetForm();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement du tableau de bord...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <div className="flex items-center space-x-2 text-red-600 mb-4">
            <AlertCircle size={20} />
            <span className="font-semibold">Erreur</span>
          </div>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={fetchCasinos}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg ${
          notification.type === 'success' 
            ? 'bg-green-500 text-white' 
            : 'bg-red-500 text-white'
        }`}>
          {notification.message}
        </div>
      )}

      {/* Header - only show if hideHeader is false */}
      {!hideHeader && (
        <header className="bg-white shadow-sm border-b">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Tableau de Bord Admin</h1>
                <p className="text-gray-600">Gérer les offres de casino</p>
              </div>
              <div className="flex items-center space-x-2">
                <span className="bg-blue-900 text-white px-2 py-1 rounded text-lg font-bold">123</span>
                <span className="text-gray-800 font-semibold text-lg">PARIEZ</span>
              </div>
            </div>
          </div>
        </header>
      )}

      <div className="container mx-auto px-4 py-8">
        {/* Show mock data warning if using fallback */}
        {usingMockData && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  <strong>Mode démonstration:</strong> Vous visualisez des données d'exemple. Les modifications ne seront pas sauvegardées. La connexion à l'API sera rétablie prochainement.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
          <h2 className="text-xl font-semibold text-gray-900">
            Casinos ({casinos.length})
          </h2>
          <div className="flex space-x-2">
            {usingMockData && (
              <button
                onClick={() => {
                  if (window.confirm('Êtes-vous sûr de vouloir réinitialiser toutes les données ? Cette action ne peut pas être annulée.')) {
                    sharedData.clearData();
                    fetchCasinos();
                    showNotification('Données réinitialisées avec succès !');
                  }
                }}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm"
              >
                Réinitialiser les données
              </button>
            )}
            {!isAddingNew && !editingId && (
              <button
                onClick={() => setIsAddingNew(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
              >
                <Plus size={20} />
                <span>Ajouter Nouveau Casino</span>
              </button>
            )}
          </div>
        </div>

        {/* Add/Edit Form */}
        {(isAddingNew || editingId) && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h3 className="text-lg font-semibold mb-4">
              {isAddingNew ? 'Ajouter Nouveau Casino' : 'Modifier Casino'}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom du Casino *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: NetBet"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URL du Logo *
                </label>
                <input
                  type="url"
                  value={formData.logo}
                  onChange={(e) => setFormData({...formData, logo: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="https://example.com/logo.png"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Note (1-10)
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  step="0.5"
                  value={formData.rating}
                  onChange={(e) => setFormData({...formData, rating: parseFloat(e.target.value)})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Score (1-10)
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  step="0.5"
                  value={formData.score}
                  onChange={(e) => setFormData({...formData, score: parseFloat(e.target.value)})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bonus Principal *
                </label>
                <input
                  type="text"
                  value={formData.mainBonus}
                  onChange={(e) => setFormData({...formData, mainBonus: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: Bonus Sans Dépôt 200€"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bonus Secondaire (optionnel)
                </label>
                <input
                  type="text"
                  value={formData.secondaryBonus}
                  onChange={(e) => setFormData({...formData, secondaryBonus: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: 100% Premier Dépôt Max 1000€"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bonus Extra (optionnel)
                </label>
                <input
                  type="text"
                  value={formData.extraBonus}
                  onChange={(e) => setFormData({...formData, extraBonus: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: 200 Tours Gratuits"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Dépôt Minimum (optionnel)
                </label>
                <input
                  type="text"
                  value={formData.minDeposit}
                  onChange={(e) => setFormData({...formData, minDeposit: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: 10€"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URL du Site Casino *
                </label>
                <input
                  type="url"
                  value={formData.url}
                  onChange={(e) => setFormData({...formData, url: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="https://casino-example.com"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Autorisé par (optionnel)
                </label>
                <input
                  type="text"
                  value={formData.authorized}
                  onChange={(e) => setFormData({...formData, authorized: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: ARJEL"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Détails des Conditions
                </label>
                <textarea
                  value={formData.termsDetails}
                  onChange={(e) => setFormData({...formData, termsDetails: e.target.value})}
                  rows="3"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Description détaillée des termes et conditions..."
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={handleCancel}
                disabled={saving}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center space-x-2 disabled:opacity-50"
              >
                <X size={16} />
                <span>Annuler</span>
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center space-x-2 disabled:opacity-50"
              >
                <Save size={16} />
                <span>{saving ? 'Sauvegarde...' : 'Sauvegarder'}</span>
              </button>
            </div>
          </div>
        )}

        {/* Casino list with drag and drop */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Liste des opérateurs ({casinos.length})
              </h3>
              {casinos.filter(c => c.hidden).length > 0 && (
                <p className="text-sm text-orange-600">
                  {casinos.filter(c => c.hidden).length} opérateur(s) masqué(s)
                </p>
              )}
            </div>
            <div className="flex items-center space-x-4">
              <p className="text-sm text-gray-500">
                <Eye size={14} className="inline mr-1" /> = visible | 
                <EyeOff size={14} className="inline mx-1" /> = masqué
              </p>
            </div>
          </div>
          
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={casinos.map(c => c.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-3">
                {casinos.map((casino) => (
                  <SortableItem
                    key={casino.id}
                    casino={casino}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onToggleVisibility={handleToggleVisibility}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;